import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Check, Info } from 'lucide-react';

const EcommerceSelectionGuide = () => {
  return (
    <section className="py-16 md:py-28 bg-gradient-to-b from-blue-50 to-white">
      <div className="container">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-5xl font-bold mb-6">
            Die richtige E-Commerce Plattform für dein Business
          </h2>
          <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
            Jedes Unternehmen hat individuelle Anforderungen – wir helfen dir, die optimale Lösung für deine spezifischen Bedürfnisse zu finden.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-12">
          <Card className="border-2 border-blue-200 bg-white hover:shadow-lg transition-all duration-300">
            <CardContent className="p-8">
              <div className="flex justify-between items-start mb-6">
                <h3 className="text-2xl font-bold text-blue-700">Individual-Plattform</h3>
                <div className="rounded-full bg-blue-100 p-2">
                  <Info className="h-5 w-5 text-blue-700" />
                </div>
              </div>

              <div className="flex items-center mb-6">
                <span className="text-xl font-bold mr-2">Ideal für:</span>
                <span className="bg-blue-50 text-blue-700 text-sm font-medium py-1 px-3 rounded-full">
                  Enterprise & spezielle Anforderungen
                </span>
              </div>

              <ul className="space-y-4 mb-8">
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold mb-1">Komplexe & spezifische Geschäftsprozesse</p>
                    <p className="text-sm text-gray-600">Maßgeschneiderte Lösungen für einzigartige Anforderungen ohne Kompromisse.</p>
                  </div>
                </li>
                
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold mb-1">Volle technische Kontrolle</p>
                    <p className="text-sm text-gray-600">Vollständige Kontrolle über Technologie, Hosting und Sicherheit.</p>
                  </div>
                </li>
                
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold mb-1">Tiefe System-Integration</p>
                    <p className="text-sm text-gray-600">Maßgeschneiderte Verbindungen zu allen internen Systemen und Prozessen.</p>
                  </div>
                </li>
              </ul>
              
              <div className="bg-gray-50 p-4 rounded-md">
                <p className="text-sm font-medium">
                  <span className="text-blue-700">Entwicklungszeit:</span> 3-8 Monate
                </p>
                <p className="text-sm">
                  <span className="text-blue-700">Typische Investition:</span> Mittel bis hoch
                </p>
              </div>
            </CardContent>
          </Card>

          <Card className="border-2 border-green-200 bg-white hover:shadow-lg transition-all duration-300">
            <CardContent className="p-8">
              <div className="flex justify-between items-start mb-6">
                <h3 className="text-2xl font-bold text-green-700">Shopware</h3>
                <div className="rounded-full bg-green-100 p-2">
                  <Info className="h-5 w-5 text-green-700" />
                </div>
              </div>

              <div className="flex items-center mb-6">
                <span className="text-xl font-bold mr-2">Ideal für:</span>
                <span className="bg-green-50 text-green-700 text-sm font-medium py-1 px-3 rounded-full">
                  Mittelstand & wachsende Unternehmen
                </span>
              </div>

              <ul className="space-y-4 mb-8">
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold mb-1">Flexibilität mit Standard-Basis</p>
                    <p className="text-sm text-gray-600">Solide Basis mit umfangreichen Anpassungsmöglichkeiten und Erweiterungen.</p>
                  </div>
                </li>
                
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold mb-1">B2B & B2C Funktionen</p>
                    <p className="text-sm text-gray-600">Optimale Balance zwischen Endkunden- und Business-Features.</p>
                  </div>
                </li>
                
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold mb-1">Starke Community & Ökosystem</p>
                    <p className="text-sm text-gray-600">Breites Netzwerk an Entwicklern, Plugins und Support-Ressourcen.</p>
                  </div>
                </li>
              </ul>
              
              <div className="bg-gray-50 p-4 rounded-md">
                <p className="text-sm font-medium">
                  <span className="text-green-700">Entwicklungszeit:</span> 2-4 Monate
                </p>
                <p className="text-sm">
                  <span className="text-green-700">Typische Investition:</span> Niedrig bis mittel
                </p>
              </div>
            </CardContent>
          </Card>

          <Card className="border-2 border-purple-200 bg-white hover:shadow-lg transition-all duration-300">
            <CardContent className="p-8">
              <div className="flex justify-between items-start mb-6">
                <h3 className="text-2xl font-bold text-purple-700">Shopify</h3>
                <div className="rounded-full bg-purple-100 p-2">
                  <Info className="h-5 w-5 text-purple-700" />
                </div>
              </div>

              <div className="flex items-center mb-6">
                <span className="text-xl font-bold mr-2">Ideal für:</span>
                <span className="bg-purple-50 text-purple-700 text-sm font-medium py-1 px-3 rounded-full">
                  Startups & kleinere Unternehmen
                </span>
              </div>

              <ul className="space-y-4 mb-8">
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold mb-1">Schneller Markteintritt</p>
                    <p className="text-sm text-gray-600">Sofort einsatzbereit mit minimaler Implementierungszeit und -aufwand.</p>
                  </div>
                </li>
                
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold mb-1">Einfache Handhabung</p>
                    <p className="text-sm text-gray-600">Intuitives Backend für selbstständige Verwaltung und Pflege.</p>
                  </div>
                </li>
                
                <li className="flex items-start">
                  <Check className="h-5 w-5 text-green-500 mr-3 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold mb-1">Starkes App-Ökosystem</p>
                    <p className="text-sm text-gray-600">Tausende von Erweiterungen für nahezu jeden Bedarf verfügbar.</p>
                  </div>
                </li>
              </ul>
              
              <div className="bg-gray-50 p-4 rounded-md">
                <p className="text-sm font-medium">
                  <span className="text-purple-700">Entwicklungszeit:</span> 1-2 Monate
                </p>
                <p className="text-sm">
                  <span className="text-purple-700">Typische Investition:</span> Niedrig
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="max-w-4xl mx-auto bg-blue-50 p-6 md:p-8 rounded-xl">
          <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-8">
            <div className="flex-shrink-0 bg-blue-100 rounded-full p-4 md:p-5 self-center md:self-start">
              <Info className="h-6 w-6 md:h-8 md:w-8 text-blue-700" />
            </div>
            <div>
              <h3 className="text-xl md:text-2xl font-bold mb-3 text-center md:text-left">Unsere Plattform-Beratung</h3>
              <p className="text-gray-700 mb-4">
                Als technologieunabhängige Agentur beraten wir dich objektiv, welche Plattform am besten zu deinen 
                individuellen Anforderungen, Budget und Zukunftsplänen passt. In einem kostenlosen Strategie-Gespräch 
                analysieren wir deine Bedürfnisse und empfehlen die optimale Lösung.
              </p>
              <p className="text-blue-800 font-medium">
                Vereinbare jetzt dein kostenfreies Beratungsgespräch und finde die perfekte E-Commerce Lösung für dein Business.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EcommerceSelectionGuide; 